import { FC, useMemo, useState } from 'react';

import { Box, Image, useWindowInnerWidth } from '~shared/ui';
import { useTranslation } from '~shared/lib/i18n/i18n';
import defaultImage from '~shared/assets/defaultImage100.svg';

import { IPortfilioPhoto } from '../../api';

export interface PortfolioViewProps {
  type: string;
  description: string;
  images: IPortfilioPhoto[];
  editSlot: any;
}

const ApiUrl = import.meta.env.VITE_API_URL;

export const PortfolioView: FC<PortfolioViewProps> = ({ type, description, images, editSlot }) => {
  const { t } = useTranslation();
  const windowWidth = useWindowInnerWidth();

  const baseUrl = `${ApiUrl}/kindergarten/organization/photo`;
  const [url1, setUrl1] = useState(`${baseUrl}/sm/${images?.[0]?.name}?t=${Date.now()}`);
  const [url2, setUrl2] = useState(`${baseUrl}/sm/${images?.[1]?.name}?t=${Date.now()}`);
  const [url3, setUrl3] = useState(`${baseUrl}/sm/${images?.[2]?.name}?t=${Date.now()}`);
  const [step, setStep] = useState(0);

  useMemo(() => {
    setUrl1(`${baseUrl}/sm/${images?.[0]?.name}?t=${Date.now()}`);
    setUrl2(`${baseUrl}/sm/${images?.[1]?.name}?t=${Date.now()}`);
    setUrl3(`${baseUrl}/sm/${images?.[2]?.name}?t=${Date.now()}`);
  }, [images?.[0]?.name, images?.[1]?.name, images?.[2]?.name]);

  const onError = (id: number) => {
    if (id === 0) {
      setUrl1(defaultImage);
    }

    if (id === 1) {
      setUrl2(defaultImage);
    }

    if (id === 2) {
      setUrl3(defaultImage);
    }
  };

  return (
    <Box>
      <div className="flex gap-5 justify-between md:flex-col">
        <div className="grid gap-3">
          <p className="text-[15px] leading-[15px]">{t(`organization:${type}`)}</p>
          <p className="text-[12px] leading-[14px]">{description}</p>
        </div>

        <div className="flex gap-5 items-center xs:gap-2">
          <Image.PreviewGroup
            items={images?.map((a) => `${baseUrl}/lg/${a.name}`)}
            preview={{
              onChange: (current) => setStep(current),
              imageRender: () => {
                return (
                  <div className="grid gap-5 w-min p-5">
                    <Image
                      width={windowWidth > 768 ? 600 : '80vw'}
                      height={windowWidth > 768 ? 600 : '60vh'}
                      preview={false}
                      src={`${baseUrl}/lg/${images?.[step]?.name}`}
                      className="rounded-[10px] object-cover max-h-[600px]"
                    />
                    <p className="bg-white rounded-[10px] text-[12px] p-[20px_30px] mb-10">
                      {description}
                    </p>
                  </div>
                );
              },
              toolbarRender: () => null,
            }}
          >
            <div className="flex gap-2.5 -m-2.5 xs:gap-2 ">
              <Image
                className="rounded object-cover"
                src={url1}
                width={windowWidth > 568 ? 100 : 70}
                height={windowWidth > 568 ? 100 : 70}
                preview={url1 !== defaultImage}
                onError={() => onError(0)}
                onClick={() => setStep(0)}
              />
              <Image
                className="rounded object-cover"
                src={url2}
                width={windowWidth > 568 ? 100 : 70}
                height={windowWidth > 568 ? 100 : 70}
                preview={url2 !== defaultImage}
                onError={() => onError(1)}
                onClick={() => setStep(1)}
              />

              <Image
                className="rounded object-cover"
                src={url3}
                width={windowWidth > 568 ? 100 : 70}
                height={windowWidth > 568 ? 100 : 70}
                preview={url3 !== defaultImage}
                onError={() => onError(2)}
                onClick={() => setStep(2)}
              />
            </div>
          </Image.PreviewGroup>

          <div className="ml-2.5"> {editSlot}</div>
        </div>
      </div>
    </Box>
  );
};
